import {
  ISelectOption,
  RootState,
  ThunkDispatch,
} from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { fetchFixturesFuture } from "core-integration/src/store/fixtures/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { TFunction } from "i18next";
import { FilterChip } from "plos/src/components/Chip";
import { PopoverSelect } from "plos/src/components/PopoverSelects";
import PitchIcon from "plos/src/img/icons/pitch.svg?react";
import { useEffect, useMemo } from "react";
import { ListBoxItem, Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useOverlayContext } from "../../../contexts/OverlayContext";
import styles from "../../FilterPopovers/filterPopover.module.css";
import type { SelectedOverlay } from "../types";
import { overlayPageConfig } from "./overlayPageConfig";

interface IOverlayOption extends ISelectOption {
  value: SelectedOverlay;
  label: string;
}

const getOverlayOptions = (t: TFunction): IOverlayOption[] => [
  {
    value: "opponent",
    label: t("pitchControls.pitchOverlay.opponent", "Opponent"),
  },
  { value: "points", label: t("pitchControls.pitchOverlay.points", "Points") },
  {
    value: "currentPrice",
    label: t("pitchControls.pitchOverlay.currentPrice", "Current Price"),
  },
  {
    value: "sellingPrice",
    label: t("pitchControls.pitchOverlay.sellingPrice", "Selling Price"),
  },
  { value: "fdr", label: t("pitchControls.pitchOverlay.fdr", "FDR") },
  { value: "form", label: t("pitchControls.pitchOverlay.form", "Form") },
  {
    value: "ownership",
    label: t("pitchControls.pitchOverlay.ownership", "Ownership"),
  },
  {
    value: "price_change",
    label: t("pitchControls.pitchOverlay.priceChange", "Price Change"),
  },
];

const OverlayDropdown = () => {
  const { t } = useTranslation("common");

  const overlayOptions = useMemo(() => getOverlayOptions(t), [t]);

  const { selectedOverlay, setSelectedOverlay, overlayPage } =
    useOverlayContext();
  const dispatch = useDispatch<ThunkDispatch>();
  const currentEvent = useSelector(getCurrentEvent);
  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  const params = useParams();
  const entryId = Number(params.entryId);
  const mine = player?.entry === entryId;

  // The FDR overlay needs upcoming fixtures and their difficulties.
  useEffect(() => {
    if (selectedOverlay === "fdr") {
      dispatch(fetchFixturesFuture());
    }
  }, [dispatch, selectedOverlay]);

  const visibleOptions = overlayOptions.filter(({ value }) => {
    // Filter by page config
    if (!overlayPageConfig[overlayPage].includes(value)) return false;
    // Hide points, form, selling price and price change values before the season starts
    if (
      (value === "points" ||
        value === "form" ||
        value === "sellingPrice" ||
        value === "price_change") &&
      !currentEvent
    )
      return false;
    // Hide points when viewing own team for a new entry / no entry
    if (
      mine &&
      value === "points" &&
      (!entry || entry.started_event > currentEvent!.id)
    )
      return false;
    return true;
  });

  const activeOption =
    visibleOptions.find((o) => o.value === selectedOverlay) ??
    visibleOptions[0] ??
    overlayOptions[0];

  const handleOverlayChange = (keys: Selection) => {
    const key = Array.from(keys)[0];
    setSelectedOverlay(key as SelectedOverlay);
  };

  return (
    <PopoverSelect
      popoverTrigger={
        <FilterChip
          label={t(
            "pitchControls.pitchOverlay.optionsLabel",
            "Overlay Options",
          )}
          selectedOption={[activeOption]}
          icon={<PitchIcon aria-hidden="true" />}
        />
      }
      handleOnChange={handleOverlayChange}
      selectedKey={activeOption.value}
    >
      {visibleOptions.map(({ value, label }) => (
        <ListBoxItem
          key={value}
          id={value}
          data-selected={value === activeOption.value}
          className={styles.popoverListItem}
          textValue={label}
        >
          {label}
        </ListBoxItem>
      ))}
    </PopoverSelect>
  );
};

export default OverlayDropdown;
